import type {
  FacetConfig,
  FacetParam,
  SearchFilters,
  SortOptions
} from '../types';
import merge from 'deepmerge';

import * as _ from './misc';

interface FacetParamState {
  facets: FacetConfig[];
  query: string | null;
}

export function getFacetParams(
  state: FacetParamState
): Record<string, FacetParam<any>> {
  return state.facets.reduce((solrFacets, facet) => {
    const { index, name, type } = facet;
    switch (type) {
      case 'min_max': {
        solrFacets[name] = merge(
          {},
          {
            type: 'query',
            q: state.query ?? '',
            facet: {
              min: `min(${index})`,
              max: `max(${index})`
            },
            domain: {
              excludeTags: name
            }
          }
        );
        break;
      }

      default:
        solrFacets[facet.name] = merge(
          {},
          {
            type: 'terms',
            field: facet.index,
            domain: facet.options?.persistCounts
              ? { excludeTags: facet.name }
              : {}
          }
        );
        break;
    }
    return solrFacets;
  }, {} as Record<string, FacetParam<any>>);
}

interface FilterParamState {
  filters: SearchFilters;
  facets: FacetConfig[];
  query: string | null;
}

export function getFilterParams(state: FilterParamState): string[] {
  const { filters, facets } = state;
  return _.filtered(
    Object.entries(filters)
      // filter out null (i.e., removed) values, as well as empty arrays
      .filter(
        ([, value]) =>
          (Array.isArray(value) && value.length) || (value ?? null) !== null
      )
      // now, we can safely assume the value exists and isn't an empty array
      .map(([field, value]) => {
        const facet = facets.find(x => x.index === field);

        const query = Array.isArray(value)
          ? (value ?? []).join(facet?.options?.useAnd ? ' AND ' : ' OR ')
          : value;

        return query !== ''
          ? (facet ? `{!tag=${facet.name}}` : '') + `${field}:(${query})`
          : null;
      })
  );
}

interface SortParamState {
  sort: SortOptions;
}

export function getSortParams(state: SortParamState): string | null {
  const { sort } = state;
  return sort.field && sort.direction
    ? [sort.field, sort.direction].join(' ')
    : null;
}
